"use client";

import { MessageSquare } from "lucide-react";
import { useTranslations } from "next-intl";

import type { InteractionLine } from "@/features/judge-authoring/types";

/** How to flush stdout, per language: the line a missing flush costs. */
const FLUSH_LINES = [
  ["C++", "cout << endl;"],
  ["C", "fflush(stdout);"],
  ["Python", "print(..., flush=True)"],
  ["Java", "System.out.flush();"],
] as const;

/**
 * `EX-IAC01`: the three-line box before an interactive problem's statement,
 * from the contestant board: both programs run at once, read then print,
 * flush every line, with the flush call for the common languages.
 */
export function InteractiveExplainer() {
  const t = useTranslations("judge.interactive");
  return (
    <section
      aria-labelledby="interactive-explainer"
      className="flex gap-3.5 rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 leading-relaxed"
      data-testid="interactive-explainer"
    >
      <span aria-hidden className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-800">
        <MessageSquare className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 space-y-2">
        <h2 id="interactive-explainer" className="text-[15px] font-bold text-neutral-900">
          {t("boxTitle")}
        </h2>
        <ol className="space-y-1.5">
          {(["box1", "box2", "box3"] as const).map((key, index) => (
            <li key={key} className="flex gap-2.5 text-sm text-neutral-700">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-indigo-100 bg-white text-[11px] font-bold tabular-nums text-indigo-700">
                {index + 1}
              </span>
              <span>{t(key)}</span>
            </li>
          ))}
        </ol>
        <ul className="flex flex-wrap gap-1.5 pt-0.5" aria-label={t("flushLabel")}>
          {FLUSH_LINES.map(([language, code]) => (
            <li key={language} className="rounded-md border border-indigo-100 bg-white px-2 py-1 font-mono text-[12px] text-indigo-900">
              <span className="mr-1 font-sans font-semibold text-neutral-500">{language}</span>
              {code}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/**
 * `EX-IAC01`: a sample as the conversation a contestant reads, the judge's
 * lines on the left, the program's on the right, in place of the sample's
 * input and output (those are the interactor's own files and never sent).
 */
export function SampleConversation({ lines, index }: { lines: readonly InteractionLine[]; index: number }) {
  const t = useTranslations("judge.interactive");
  return (
    <div className="space-y-2">
      <span className="text-xs font-medium uppercase tracking-wide text-[rgb(var(--muted-foreground))]">
        {t("sampleConversation", { n: index + 1 })}
      </span>
      {lines.length === 0 ? (
        <p className="rounded-xl bg-neutral-50 p-3 text-sm text-[rgb(var(--muted-foreground))]">{t("sampleNoConversation")}</p>
      ) : (
        <ol className="space-y-1.5 rounded-xl bg-neutral-50 p-3" data-testid="sample-conversation">
          {lines.map((line, i) => {
            const judge = line.from === "judge";
            return (
              <li key={i} className={judge ? "flex justify-start" : "flex justify-end"}>
                <span className={judge ? "flex max-w-[80%] flex-col items-start gap-0.5" : "flex max-w-[80%] flex-col items-end gap-0.5"}>
                  <span className="text-[11px] text-neutral-500">{judge ? t("judge") : t("you")}</span>
                  <span
                    className={
                      judge
                        ? "whitespace-pre rounded-lg bg-white px-3 py-1.5 font-mono text-sm text-neutral-800 shadow-sm ring-1 ring-neutral-200"
                        : "whitespace-pre rounded-lg bg-neutral-900 px-3 py-1.5 font-mono text-sm text-neutral-50"
                    }
                  >
                    {line.text === "" ? " " : line.text}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
